import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["title", "container", "leftButton", "rightButton"]
  static values  = {
    offset:    { type: Number,  default: 160 },
    threshold: { type: Number,  default: 0.5 }
  }

  connect () {
    this.setupIntersectionObserver()
    this.updateButtonStates()
    this.updateTermTitleFromScroll()
  }

  disconnect () {
    this.cleanupObserver()
  }

  containerTargetConnected(container) {
    this.boundHandleScroll = this.handleScroll.bind(this)
    container.addEventListener('scroll', this.boundHandleScroll)
    this.updateTermTitleFromScroll()
  }

  containerTargetDisconnected(container) {
    if (this.boundHandleScroll) {
      container.removeEventListener('scroll', this.boundHandleScroll)
    }
  }

  handleScroll() {
    this.updateButtonStates()
    this.updateTermTitleFromScroll()
  }

  updateTermTitleFromScroll() {
    if (!this.hasContainerTarget) return

    const container = this.containerTarget
    const containerRect = container.getBoundingClientRect()
    const termSections = container.querySelectorAll('[data-term-name]')
    if (termSections.length === 0) return

    const isHorizontalScroll = container.scrollWidth > container.clientWidth

    let currentTerm = null
    let currentTermPosition = -Infinity

    termSections.forEach(el => {
      const elRect = el.getBoundingClientRect()

      if (isHorizontalScroll) {
        const elLeftRelativeToContainer = elRect.left - containerRect.left
        const triggerPoint = this.offsetValue + 50
        if (elLeftRelativeToContainer <= triggerPoint && elLeftRelativeToContainer > currentTermPosition) {
          currentTerm = el
          currentTermPosition = elLeftRelativeToContainer
        }
      } else {
        const elTopRelativeToContainer = elRect.top - containerRect.top
        const triggerPoint = containerRect.height * 0.5
        if (elTopRelativeToContainer <= triggerPoint && elTopRelativeToContainer > currentTermPosition) {
          currentTerm = el
          currentTermPosition = elTopRelativeToContainer
        }
      }
    })

    if (currentTerm) {
      const termName = currentTerm.getAttribute('data-term-name')
      if (termName) this.updateTitles(termName)
    }
  }

  scrollLeft () {
    if (!this.hasContainerTarget) return

    this.containerTarget.scrollBy({
      left: -this.offsetValue * 2.4,
      behavior: "smooth"
    })

    setTimeout(() => this.updateButtonStates(), 300)
  }

  scrollRight () {
    if (!this.hasContainerTarget) return

    this.containerTarget.scrollBy({
      left: this.offsetValue * 2.4,
      behavior: "smooth"
    })

    setTimeout(() => this.updateButtonStates(), 300)
  }

  scrollToCurrentWeek () {
    const currentWeek = document.querySelector("[data-current-week]")

    const container = this.containerTarget || currentWeek.closest(".overflow-auto")
    if (!container) return

    if (container && currentWeek) {
      const containerRect = container.getBoundingClientRect()
      const weekRect = currentWeek.getBoundingClientRect()

      const weekLeftRelativeToContainer = (weekRect.left - containerRect.left) + container.scrollLeft

      const stickyColumnWidth = this.offsetValue + 5
      const targetScrollLeft = weekLeftRelativeToContainer - stickyColumnWidth

      container.scrollTo({
        left: Math.max(0, targetScrollLeft),
        behavior: "smooth"
      })
    }
  }

  setupIntersectionObserver () {
    this.termEls = {}
    const termSections = document.querySelectorAll('[id^="term_"]')
    termSections.forEach(section => {
      this.termEls[`#${section.id}`] = section
    })

    if (Object.keys(this.termEls).length === 0) {
      return
    }

    this.cleanupObserver()

    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return
          const termName = entry.target.getAttribute('data-term-name')
          if (termName) this.updateTitles(termName)
        })
      },
      {
        root: this.hasContainerTarget ? this.containerTarget : null,
        rootMargin: "0px",
        threshold: this.thresholdValue
      }
    )

    Object.values(this.termEls).forEach(el => {
      if (el) this.observer.observe(el)
    })
  }

  cleanupObserver() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  updateTitles (text) {
    this.titleTargets.forEach(title => {
      if (title) {
        title.textContent = text
      }
    })
  }

  updateButtonStates() {
    if (!this.hasContainerTarget) return

    const container = this.containerTarget
    const scrollLeft = container.scrollLeft
    const maxScrollLeft = container.scrollWidth - container.clientWidth

    if (this.hasLeftButtonTarget) {
      this.leftButtonTarget.disabled = scrollLeft <= 0
    }

    if (this.hasRightButtonTarget) {
      this.rightButtonTarget.disabled = scrollLeft >= maxScrollLeft
    }
  }
}
